// frontend/src/hooks/useIdentity.js
//
// The standing identity / duplicate-review surface. Read the suspected
// cross-origin duplicate item pairs (a channel-origin row + its ERP/native
// twin the resolver judges to be the same product), and merge a confirmed
// pair. Both route through apiJson, so store-scoping (X-Store-ID) and
// structured errors are handled centrally — see queries/api.js.

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../contexts/AuthContext';
import { apiJson, readResult } from '../queries/api';
import { DATA } from '../queries/dataChanged';
import { identityDuplicates as identityKey } from '../queries/keys';
import { useApiMutation } from '../queries/useApiMutation';

// A merge/dismiss can race the pair leaving the list — the sibling pair was just
// resolved, a sweep ran, or an item was deleted. The backend answers 404
// `item_not_found`. That's not a failure: the pair is already gone, which is the
// outcome the merchant wanted. Fold it into success so the row leaves quietly and
// the caller can note it, instead of surfacing a red error for a no-op.
const ALREADY_RESOLVED = { alreadyResolved: true };
async function resolvingRace(request) {
  try {
    return await request;
  } catch (err) {
    if (err?.status === 404 && err?.code === 'item_not_found') return ALREADY_RESOLVED;
    throw err;
  }
}

export function useDuplicates() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;

  const query = useQuery({
    queryKey: identityKey.all(storeId),
    queryFn: () => apiJson.get('/api/identity/duplicates/'),
    enabled: !!storeId,
    staleTime: 30_000,
  });

  const { data, ...result } = readResult(query, t);
  return { duplicates: data?.duplicates, count: data?.count, ...result };
}

// The merged pair leaves the list, and the loser's item is folded into the
// survivor.
export function useMergeDuplicate() {
  return useApiMutation({
    mutationFn: ({ survivorId, loserId }) =>
      resolvingRace(apiJson.post('/api/identity/merge/', {
        survivor_id: survivorId,
        loser_id: loserId,
      })),
    changes: [DATA.identity, DATA.items],
  });
}

// Record that a pair is NOT the same product — drops it from the list and stops
// the nightly sweep auto-merging it.
export function useDismissDuplicate() {
  return useApiMutation({
    mutationFn: ({ idA, idB }) =>
      resolvingRace(apiJson.post('/api/identity/dismiss/', { id_a: idA, id_b: idB })),
    changes: [DATA.identity],
  });
}
